import { createApp } from "vue";
import App from "./App.vue";
import router from "./router";
import axios from "axios";
// Vuetify
import "vuetify/styles"; // Importă stilurile Vuetify
import { createVuetify } from "vuetify";
import * as components from "vuetify/components";
import * as directives from "vuetify/directives";
import { aliases, mdi } from "vuetify/iconsets/mdi"; // Importă setul de iconițe mdi
import "@mdi/font/css/materialdesignicons.css"; // Importează stilurile mdi
import { VDataTable, VDataTableServer } from "vuetify/lib/components/VDataTable";
import "vuetify/dist/vuetify.min.css";
import "./main.css";
import { createI18nInstance } from "./plugins/i18n.js";
import { VUETIFY_DEFAULTS } from "./plugins/vuetifyDefaults.mjs";
import { getActiveCompanyId } from "@/Utile/companyContext.js";
import { clearAuthStorage } from "@/Utile/authStorage.mjs";
import { hasAuthSession, isSessionEndResponse, readLegacyToken } from "@/Utile/authSession.mjs";

const apiBaseUrl = process.env.VUE_APP_API_BASE || "/api";
const filesBaseUrl = process.env.VUE_APP_FILES_BASE || "/uploads";

// Configurare baseURL pentru Axios
axios.defaults.baseURL = apiBaseUrl;
// H3: sesiunea este cookie-ul httpOnly `miridar_session`, trimis de browser (și cross-origin, prin withCredentials).
axios.defaults.withCredentials = true;
axios.interceptors.request.use(
  (config) => {
    // Doar un token rămas de la un login de dinainte de H3 PR 2 mai pleacă drept Bearer, până expiră (max 24 h).
    const legacyToken = readLegacyToken();
    if (legacyToken) {
      config.headers.Authorization = `Bearer ${legacyToken}`;
    }

    const url = config.url || "";
    const needsCompanyId = /checklist|employee|company/i.test(url);
    if (needsCompanyId) {
      const companyId = getActiveCompanyId();
      if (companyId) {
        if (config.method && config.method.toLowerCase() === "get") {
          config.params = { company_id: companyId, ...(config.params || {}) };
        } else if (config.data && typeof config.data === "object" && !Array.isArray(config.data)) {
          if (config.data.company_id === undefined || config.data.company_id === null) {
            config.data.company_id = companyId;
          }
        } else {
          config.params = { company_id: companyId, ...(config.params || {}) };
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Adăugăm interceptorul Axios pentru a gestiona token-ul expirat
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Sesiune expirată, revocată sau fără cookie: ștergem starea locală și trimitem utilizatorul la login.
    // `protectedRoute` acoperă logout-ul din alt tab, care a șters deja markerul comun.
    const protectedRoute = Boolean(router.currentRoute.value?.meta?.requiresAuth);
    if (isSessionEndResponse(error.response, { signedIn: hasAuthSession(), protectedRoute })) {
      clearAuthStorage();
      // Redirecționăm utilizatorul la pagina de login
      router.push({ name: "Login" });
    }
    return Promise.reject(error);
  }
);

const vuetify = createVuetify({
  components: {
    ...components,
    VDataTable,
    VDataTableServer,
  },
  directives,
  defaults: VUETIFY_DEFAULTS,
  icons: {
    defaultSet: "mdi",
    aliases,
    sets: {
      mdi,
    },
  },
  theme: {
    defaultTheme: "light",
    themes: {
      light: {
        colors: {
          primary: "#2c3e50",
          secondary: "#746454",
          tertiary: "#24c8c6",
          quaternary: "#5b6d66",
          info: "#1e88e5",
          success: "#2e7d32",
          warning: "#fb8c00",
          error: "#dc3545",
          neutral: "#F0EEE9",
          background: "#F0EEE9",
          surface: "#ffffff",
        },
      },
      dark: {
        dark: true,
        colors: {
          primary: "#7fa8c9",
          secondary: "#c0a090",
          tertiary: "#26d4d2",
          quaternary: "#8aada3",
          info: "#64b5f6",
          success: "#66bb6a",
          warning: "#ffa726",
          error: "#ef5350",
          neutral: "#1e2229",
          background: "#111318",
          surface: "#1c202a",
        },
      },
    },
  },
});

const app = createApp(App);
const i18n = createI18nInstance();

app.config.globalProperties.$filesBase = filesBaseUrl;
app.provide("filesBase", filesBaseUrl);

app.use(router).use(vuetify).use(i18n).mount("#app");
